import React, {useCallback, useState} from 'react';
import {Trans, useTranslation} from 'next-i18next';
import {
	Button,
	FormControl,
	FormLabel,
	Input,
	ListItem,
	Text,
	UnorderedList,
	VStack,
} from '@tempo/core';
import {ArrowRightIcon} from '@tempo/icons';
import ProviderSearchInput from '../../components/ProviderSearchInput';
import {useClaims} from '@deezer/next/contexts/main/hooks';
import type {FormFieldChange, ProviderFormData} from '../../types';
import type {ProviderSearchResult} from '@deezer/next/services/api/Starmania/requests/search/types';
import TitleRow from '@components/PageElements/PageHeader/TitleRow';
import FooterRow from '@components/PageElements/FooterRow';
import PageLayout from '@deezer/next/shared/layouts/PageLayout';
import FormSectionTitle from '@components/PageElements/FormSectionTitle';
import FormContainer from '@components/PageElements/FormContainer';
import FormSectionContainer from '@components/PageElements/FormSectionContainer';
import FormSection from '@components/PageElements/FormSection';

type NewProviderFormCardProps = {
	formData: ProviderFormData;
	onFormFieldChange: FormFieldChange<ProviderFormData>;
	onSubmit: () => void;
	onBack: () => void;
	isSubmitting: boolean;
};

const NewProviderFormCard = ({
	formData,
	onFormFieldChange,
	onSubmit,
	onBack,
	isSubmitting,
}: NewProviderFormCardProps) => {
	const {t} = useTranslation('d4c');
	const claims = useClaims();
	const [selectedProvider, setSelectedProvider] = useState<
		ProviderSearchResult | undefined
	>(formData.provider);
	const handleUPC1Change = (e: React.ChangeEvent<HTMLInputElement>) =>
		onFormFieldChange('providerUpc1', e.target.value);
	const handleUPC2Change = (e: React.ChangeEvent<HTMLInputElement>) =>
		onFormFieldChange('providerUpc2', e.target.value);

	const handleSelectedProviderChange = useCallback(
		(item?: ProviderSearchResult) => {
			onFormFieldChange('provider', item);
			setSelectedProvider(item);
		},
		[onFormFieldChange],
	);

	const handleSubmit = () => {
		if (isSubmitting) return;
		onSubmit();
	};

	return (
		<PageLayout
			data-testid="new-provider-card"
			header={<TitleRow title={t('requestaccess_title_newrequest_d4c-web')} />}
			footer={
				<FooterRow
					rightElements={
						<>
							{claims.length > 0 && (
								<Button
									data-testid="back-button"
									variant="outline"
									onClick={onBack}
									size="small"
									leftIcon={<ArrowRightIcon transform="scaleX(-1)" />} // todo fix that
								>
									{t('generic_action_back_d4c-web')}
								</Button>
							)}
							<Button
								data-testid="send-request-button"
								isDisabled={
									!formData.provider?.name ||
									!formData.providerUpc1 ||
									!formData.providerUpc2
								}
								isLoading={isSubmitting}
								size="small"
								onClick={handleSubmit}
							>
								{t('generic_action_sendrequest_d4c-web')}
							</Button>
						</>
					}
				/>
			}
		>
			<FormContainer>
				<FormSection>
					<FormSectionTitle
						title={t('deliverymonitoring_title_provider_d4c-web')}
					/>
					<VStack align="left" spacing="spacing.s">
						<Text>
							{t(
								'requestaccessdistributor_text_toclaimartistasdistributorprovide_d4c-web',
							)}
						</Text>
						<UnorderedList>
							<ListItem>
								<Trans
									components={{
										strong: <strong />,
									}}
								>
									{t('requestaccessdistributor_text_livereleaseupcs_d4c-web')}
								</Trans>
							</ListItem>
							<ListItem>
								{t(
									'requestaccessdistributor_text_releasedeliveredbyyoursystem_d4c-web',
								)}
							</ListItem>
						</UnorderedList>
						<Text>
							{t(
								'requestaccessdistributor_text_ifdifferentnameredeliverorcreateaccount_d4c-web',
							)}
						</Text>
						<Text>
							{t(
								'requestaccessdistributor_text_contactinternadmintorequestaccess_d4c-web',
							)}
						</Text>
					</VStack>
					<FormSectionContainer>
						<FormControl isRequired={true}>
							<FormLabel htmlFor="provider-search-name">
								{t('requestaccessprovider_text_providername_d4c-web')}
							</FormLabel>
							<ProviderSearchInput
								data-testid="claim-new-provider-search"
								id="provider-name-search"
								placeholder={t('pitchingtool_text_enterprovidername_d4c-web')}
								selectedProvider={selectedProvider}
								onSelectedProviderChange={handleSelectedProviderChange}
							/>
						</FormControl>
						<FormControl isRequired={true}>
							<FormLabel htmlFor="upc_1">
								{t('requestaccesslabel_text_entertwoofyourartistsupcs_d4c-web')}
							</FormLabel>
							<Input
								data-testid="claim-new-provider-upc-1"
								id="upc_1"
								type="text"
								value={formData.providerUpc1}
								onChange={handleUPC1Change}
								disabled={!formData.provider?.name}
							/>
						</FormControl>
						<FormControl isRequired={true}>
							<Input
								data-testid="claim-new-provider-upc-2"
								id="upc_2"
								type="text"
								value={formData.providerUpc2}
								onChange={handleUPC2Change}
								disabled={!formData.provider?.name}
							/>
						</FormControl>
					</FormSectionContainer>
				</FormSection>
			</FormContainer>
		</PageLayout>
	);
};

export default NewProviderFormCard;
